import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import RichTextEditor from '../../components/admin/RichTextEditor';
import PageHeader from '../../components/admin/PageHeader';
import LoadingState from '../../components/admin/LoadingState';

interface LegalPageFormData {
  slug: string;
  title: string;
  content: string;
  meta_description: string;
  published: boolean;
}

const LegalPageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<LegalPageFormData>({
    slug: '',
    title: '',
    content: '',
    meta_description: '',
    published: true
  });

  useEffect(() => {
    if (id) {
      fetchPage();
    }
  }, [id]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('legal_pages')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFormData({
          slug: data.slug,
          title: data.title,
          content: data.content,
          meta_description: data.meta_description,
          published: data.published
        });
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      toast.error('Erreur lors du chargement de la page');
      navigate('/admin/legal-pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSaving(true);

      const { error } = await supabase
        .from('legal_pages')
        .update({
          title: formData.title,
          content: formData.content,
          meta_description: formData.meta_description,
          published: formData.published
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Page mise à jour avec succès');
      navigate('/admin/legal-pages');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open(`/${formData.slug}`, '_blank');
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Modifier la page"
          icon={<FileText className="w-8 h-8 text-primary-600" />}
        />
        <LoadingState />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Modifier la page"
        description="Modifiez le contenu de la page légale"
        icon={<FileText className="w-8 h-8 text-primary-600" />}
        action={
          <button
            onClick={() => navigate('/admin/legal-pages')}
            className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Retour
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Slug (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL (slug)
              </label>
              <input
                type="text"
                value={formData.slug}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              />
              <p className="mt-1 text-sm text-gray-500">
                L'URL ne peut pas être modifiée
              </p>
            </div>

            {/* Meta Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description (SEO)
              </label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Description pour les moteurs de recherche (150-160 caractères)"
              />
              <p className="mt-1 text-sm text-gray-500">
                {formData.meta_description.length} / 160 caractères
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenu *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
              />
              <p className="mt-2 text-sm text-gray-500">
                Utilisez l'éditeur pour formater votre contenu. Le HTML est supporté.
              </p>
            </div>

            {/* Published Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="published" className="ml-2 text-sm font-medium text-gray-700">
                Page publiée (visible sur le site)
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button
            type="button"
            onClick={handlePreview}
            className="inline-flex items-center px-4 py-2 text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Eye size={18} className="mr-2" />
            Prévisualiser
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/legal-pages')}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LegalPageForm;
